/**
 * notificationSound — Web-Audio synthesized notification sounds + the
 * per-device Friends notification settings (sound on/off, which sound, how
 * long it plays, and whether incoming voice notes autoplay). No audio files to
 * host; everything is generated on the fly so it works offline and adds ~0 bytes.
 *
 * Settings live in localStorage under `iw_notif_settings`:
 *   { soundEnabled, soundId, soundLength, autoplay }
 *
 * soundLength lets users "extend" ANY sound (subtle or loud) so a quick chirp
 * can become a longer, more attention-grabbing alert:
 *   "short"  — single play (the classic quick cue)
 *   "medium" — repeats to ~1.6s
 *   "long"   — repeats to ~3s
 */

const LS_KEY = "iw_notif_settings";

const DEFAULTS = {
  soundEnabled: true, // a gentle chirp on new messages/voice by default
  soundId: "chirp",
  soundLength: "short", // "short" | "medium" | "long"
  volume: 1, // 0..1 master loudness for all chat alerts
  pushToTalkAutoOpen: true, // open the "Talk with…" push-to-talk popup on incoming voice
  autoplay: false, // voice notes are tap-to-play by default (no classroom distraction)
  quietHoursEnabled: false, // silence notification sounds during a nightly window
  quietStart: "21:00", // HH:MM (24h) in the user's timezone
  quietEnd: "07:00",
  soundSnoozeUntil: 0, // ms epoch; while in the future, sounds are temporarily muted
};

// Per-friend sound overrides live in their own localStorage key so they don't
// bloat the main settings blob: { [friendId]: soundId }.
const FRIEND_LS_KEY = "iw_friend_sounds";

export function getFriendSounds() {
  try { return JSON.parse(localStorage.getItem(FRIEND_LS_KEY) || "{}") || {}; }
  catch { return {}; }
}

export function getFriendSound(friendId) {
  if (!friendId) return null;
  return getFriendSounds()[friendId] || null;
}

// Pass soundId to assign, or null/"" to clear (fall back to the default sound).
export function setFriendSound(friendId, soundId) {
  if (!friendId) return;
  const map = getFriendSounds();
  if (soundId) map[friendId] = soundId;
  else delete map[friendId];
  try { localStorage.setItem(FRIEND_LS_KEY, JSON.stringify(map)); } catch { /* noop */ }
  try { window.dispatchEvent(new CustomEvent("iw:friend-sounds", { detail: map })); } catch { /* noop */ }
  return map;
}

// Per-friend push-to-talk pop-up overrides: { [friendId]: true|false }.
// Unset (undefined) = follow the global `pushToTalkAutoOpen` setting.
const FRIEND_PTT_KEY = "iw_friend_ptt";

export function getFriendPTTMap() {
  try { return JSON.parse(localStorage.getItem(FRIEND_PTT_KEY) || "{}") || {}; }
  catch { return {}; }
}

// Returns true/false if this friend has an explicit override, else undefined.
export function getFriendPTT(friendId) {
  if (!friendId) return undefined;
  const v = getFriendPTTMap()[friendId];
  return typeof v === "boolean" ? v : undefined;
}

// Pass true/false to override, or null to clear (follow the global default).
export function setFriendPTT(friendId, value) {
  if (!friendId) return;
  const map = getFriendPTTMap();
  if (value === true || value === false) map[friendId] = value;
  else delete map[friendId];
  try { localStorage.setItem(FRIEND_PTT_KEY, JSON.stringify(map)); } catch { /* noop */ }
  try { window.dispatchEvent(new CustomEvent("iw:friend-ptt", { detail: map })); } catch { /* noop */ }
  return map;
}

// Resolve whether the push-to-talk pop-up should open for this friend:
// the per-friend override wins; otherwise the global setting applies.
export function shouldOpenPTT(friendId) {
  const override = getFriendPTT(friendId);
  if (typeof override === "boolean") return override;
  return getNotifSettings().pushToTalkAutoOpen !== false;
}

const LENGTH_TARGETS = { short: 0, medium: 1.6, long: 3.0 };

export const SOUND_LENGTHS = [
  { id: "short", label: "Short", hint: "Quick cue" },
  { id: "medium", label: "Longer", hint: "~1.5s" },
  { id: "long", label: "Longest", hint: "~3s" },
];

export function getNotifSettings() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    return { ...DEFAULTS, ...raw };
  } catch {
    return { ...DEFAULTS };
  }
}

export function setNotifSettings(patch) {
  const next = { ...getNotifSettings(), ...patch };
  try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* noop */ }
  try { window.dispatchEvent(new CustomEvent("iw:notif-settings", { detail: next })); } catch { /* noop */ }
  return next;
}

// ── Shared AudioContext (created lazily, resumed on demand) ──────────────────
let _ctx = null;
let _vol = 1; // current master volume, refreshed at each render from settings
function ctx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) _ctx = new AC();
  if (_ctx.state === "suspended") { try { _ctx.resume(); } catch { /* noop */ } }
  return _ctx;
}

// One short tone. `offset` shifts the whole tone later (used to repeat a sound).
function tone(c, { freq = 880, type = "sine", start = 0, dur = 0.12, gain = 0.18, glideTo = null, offset = 0 }) {
  const t0 = c.currentTime + offset + start;
  const osc = c.createOscillator();
  const g = c.createGain();
  const peak = Math.max(0.0001, gain * _vol);
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// A continuous siren that glides between two frequencies several times.
function siren(c, { lo = 650, hi = 850, type = "sawtooth", cycle = 0.6, cycles = 4, gain = 0.3, offset = 0 }) {
  const t0 = c.currentTime + offset;
  const osc = c.createOscillator();
  const g = c.createGain();
  const peak = Math.max(0.0001, gain * _vol);
  osc.type = type;
  osc.frequency.setValueAtTime(lo, t0);
  for (let i = 0; i < cycles; i++) {
    const a = t0 + i * cycle;
    osc.frequency.exponentialRampToValueAtTime(hi, a + cycle / 2);
    osc.frequency.exponentialRampToValueAtTime(lo, a + cycle);
  }
  const total = cycle * cycles;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.03);
  g.gain.setValueAtTime(peak, t0 + total - 0.06);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + total);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + total + 0.02);
}

// The selectable sound library. `dur` is the total length of one play (used by
// the "extend" repeater). `loud` groups the attention-grabbing alerts.
export const NOTIFICATION_SOUNDS = [
  // ── Subtle cues ──
  { id: "chirp", label: "Chirp", dur: 0.09, render: (c, o) => { tone(c, { freq: 1300, glideTo: 2100, dur: 0.09, type: "sine", gain: 0.16, offset: o }); } },
  { id: "beep", label: "Beep", dur: 0.1, render: (c, o) => { tone(c, { freq: 880, dur: 0.1, type: "square", gain: 0.1, offset: o }); } },
  { id: "ding", label: "Ding", dur: 0.35, render: (c, o) => { tone(c, { freq: 1568, dur: 0.35, type: "sine", gain: 0.18, offset: o }); } },
  { id: "chime", label: "Chime", dur: 0.39, render: (c, o) => { tone(c, { freq: 1046, dur: 0.16, offset: o }); tone(c, { freq: 1568, start: 0.11, dur: 0.28, offset: o }); } },
  { id: "pop", label: "Pop", dur: 0.08, render: (c, o) => { tone(c, { freq: 420, glideTo: 900, dur: 0.08, type: "sine", gain: 0.2, offset: o }); } },
  { id: "bell", label: "Bell", dur: 0.5, render: (c, o) => { tone(c, { freq: 1318, dur: 0.5, type: "triangle", gain: 0.16, offset: o }); tone(c, { freq: 2637, dur: 0.4, type: "sine", gain: 0.05, offset: o }); } },
  { id: "marimba", label: "Marimba", dur: 0.32, render: (c, o) => { tone(c, { freq: 784, dur: 0.14, type: "triangle", offset: o }); tone(c, { freq: 1176, start: 0.1, dur: 0.22, type: "triangle", offset: o }); } },
  { id: "blip", label: "Blip", dur: 0.13, render: (c, o) => { tone(c, { freq: 660, dur: 0.06, type: "square", gain: 0.09, offset: o }); tone(c, { freq: 990, start: 0.07, dur: 0.06, type: "square", gain: 0.09, offset: o }); } },
  { id: "coin", label: "Coin", dur: 0.25, render: (c, o) => { tone(c, { freq: 988, dur: 0.07, type: "square", gain: 0.1, offset: o }); tone(c, { freq: 1319, start: 0.07, dur: 0.18, type: "square", gain: 0.1, offset: o }); } },
  { id: "twinkle", label: "Twinkle", dur: 0.4, render: (c, o) => { tone(c, { freq: 1046, dur: 0.1, offset: o }); tone(c, { freq: 1318, start: 0.09, dur: 0.1, offset: o }); tone(c, { freq: 1760, start: 0.18, dur: 0.22, offset: o }); } },

  // ── Loud alerts (longer, more attention-grabbing) ──
  { id: "phonering", label: "Phone Ring", loud: true, dur: 2.0, render: (c, o) => {
      // Classic warble: two ring bursts, each a 440+480Hz dual tone.
      for (const b of [0, 1.0]) {
        tone(c, { freq: 440, dur: 0.8, type: "triangle", gain: 0.26, start: b, offset: o });
        tone(c, { freq: 480, dur: 0.8, type: "triangle", gain: 0.22, start: b, offset: o });
      }
    } },
  { id: "schoolbell", label: "School Bell", loud: true, dur: 2.0, render: (c, o) => {
      // Insistent electric bell: rapid clapper pulses with bright harmonics.
      for (let i = 0; i < 18; i++) {
        const s = i * 0.11;
        tone(c, { freq: 1200, dur: 0.07, type: "square", gain: 0.2, start: s, offset: o });
        tone(c, { freq: 1800, dur: 0.06, type: "sine", gain: 0.12, start: s, offset: o });
      }
    } },
  { id: "ambulance", label: "Ambulance", loud: true, dur: 2.4, render: (c, o) => {
      siren(c, { lo: 650, hi: 980, type: "sawtooth", cycle: 0.6, cycles: 4, gain: 0.3, offset: o });
    } },
  { id: "carhorn", label: "Car Horn", loud: true, dur: 1.5, render: (c, o) => {
      // Two firm honks, dual-tone like a real horn (A3 + F3).
      for (const b of [0, 0.85]) {
        tone(c, { freq: 440, dur: 0.55, type: "square", gain: 0.22, start: b, offset: o });
        tone(c, { freq: 349, dur: 0.55, type: "sawtooth", gain: 0.18, start: b, offset: o });
      }
    } },
  { id: "klaxon", label: "Alarm Klaxon", loud: true, dur: 2.4, render: (c, o) => {
      // Harsh descending klaxon sweeps.
      siren(c, { lo: 900, hi: 420, type: "sawtooth", cycle: 0.5, cycles: 5, gain: 0.3, offset: o });
    } },
];

function renderById(id) {
  const c = ctx();
  if (!c) return;
  const s = NOTIFICATION_SOUNDS.find((x) => x.id === id) || NOTIFICATION_SOUNDS[0];
  const settings = getNotifSettings();
  _vol = Math.max(0, Math.min(1, settings.volume ?? 1));
  if (_vol <= 0) return; // muted
  const target = LENGTH_TARGETS[settings.soundLength] || 0;
  const gap = 0.12;
  const unit = (s.dur || 0.3) + gap;
  let reps = 1;
  if (target > 0) reps = Math.max(1, Math.ceil(target / unit));
  reps = Math.min(reps, 16); // safety cap
  try {
    for (let i = 0; i < reps; i++) s.render(c, i * unit);
  } catch { /* noop */ }
}

// ── Quiet Hours ──────────────────────────────────────────────────────────
// Returns the current wall-clock minutes-since-midnight in the user's saved
// timezone (falls back to US Eastern), so quiet hours line up with their day.
function nowMinutesInUserTz() {
  let tz = "America/New_York";
  try { tz = localStorage.getItem("iw_user_tz") || tz; } catch { /* storage off */ }
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date());
    const h = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10) % 24;
    const m = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
    return h * 60 + m;
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }
}

function hhmmToMinutes(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || ""));
  if (!m) return null;
  return (parseInt(m[1], 10) % 24) * 60 + (parseInt(m[2], 10) % 60);
}

// Is "right now" inside the user's quiet-hours window? Handles overnight
// windows that wrap past midnight (e.g. 21:00 → 07:00).
export function isQuietHoursNow() {
  const s = getNotifSettings();
  if (!s.quietHoursEnabled) return false;
  const start = hhmmToMinutes(s.quietStart);
  const end = hhmmToMinutes(s.quietEnd);
  if (start == null || end == null || start === end) return false;
  const now = nowMinutesInUserTz();
  return start < end ? (now >= start && now < end) : (now >= start || now < end);
}

// ── Snooze (temporary mute) ───────────────────────────────────────────────
// Mute notification sounds for a set number of hours, then auto-resume. Unlike
// Quiet Hours (a recurring nightly window) this is a one-off that clears itself.
export function snoozeSound(hours) {
  const until = Date.now() + Math.max(0, hours) * 3600 * 1000;
  setNotifSettings({ soundSnoozeUntil: until });
  return until;
}

export function clearSnooze() {
  setNotifSettings({ soundSnoozeUntil: 0 });
}

// Returns the active snooze end time (ms) or 0 if not currently snoozed.
export function snoozeUntil() {
  const u = Number(getNotifSettings().soundSnoozeUntil || 0);
  return u > Date.now() ? u : 0;
}

// Are sounds muted right now for ANY reason (off / quiet hours / snoozed)?
export function soundsMutedNow() {
  const s = getNotifSettings();
  if (!s.soundEnabled) return true;
  if (isQuietHoursNow()) return true;
  if (Number(s.soundSnoozeUntil || 0) > Date.now()) return true;
  return false;
}

// Why sounds are muted, for labels/tooltips: "off" | "quiet" | "snooze" | null.
export function muteReason() {
  const s = getNotifSettings();
  if (!s.soundEnabled) return "off";
  if (isQuietHoursNow()) return "quiet";
  if (Number(s.soundSnoozeUntil || 0) > Date.now()) return "snooze";
  return null;
}

// Play the user's chosen notification sound (respects the on/off setting,
// Quiet Hours, and an active Snooze). Pass a friendId for their assigned sound.
export function playNotificationSound(friendId) {
  const s = getNotifSettings();
  if (!s.soundEnabled) return;
  if (isQuietHoursNow()) return; // silent during the nightly window
  if (Number(s.soundSnoozeUntil || 0) > Date.now()) return; // temporarily snoozed
  renderById(getFriendSound(friendId) || s.soundId);
}

// Preview a specific sound (ignores the on/off setting — used in the picker).
export function previewNotificationSound(id) {
  renderById(id);
}

// Should incoming voice notes autoplay? (used by the walkie-talkie + popup)
export function shouldAutoplayVoice() {
  return !!getNotifSettings().autoplay;
}
